'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useLanguage } from '@/context/LanguageContext';
import TramaMembrete from '@/components/TramaMembrete';

export default function FooterClient() {
  const { t } = useLanguage();

  const navLinks = [
    { labelKey: 'nav.home', href: '/homepage' },
    { labelKey: 'nav.services', href: '/services' },
    { labelKey: 'nav.products', href: '/products' },
    { labelKey: 'nav.compliance', href: '/compliance' },
    { labelKey: 'nav.contact', href: '/contact' },
  ];

  return (
    <footer className="relative overflow-hidden border-t border-white/10" style={{ background: 'linear-gradient(160deg, #1a2332 0%, #0B2C4A 50%, #0d3a4a 100%)' }}>
      {/* Top cyan accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #00c8d4 30%, #00c8d4 70%, transparent)' }} />

      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <TramaMembrete intensidad="baja" conGlobo={false} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[300px] rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #00c8d4 0%, transparent 70%)', transform: 'translate(20%, 30%)' }} />
        <div className="absolute top-0 left-0 w-[300px] h-[300px] rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #00c8d4 0%, transparent 70%)', transform: 'translate(-30%, -30%)' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-14">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          {/* Logo + tagline */}
          <div className="flex flex-col gap-4">
            <Link href="/homepage" aria-label="MercaliaConnect Home">
              <AppLogo
                src="/assets/images/logo_mercalia_separado-1772891797274.png"
                size={88}
                className="opacity-95 hover:opacity-100 transition-opacity"
              />
            </Link>
            <p className="text-white/40 text-xs max-w-[220px] leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {/* La mano sola invita a hacer clic: no hace falta texto. */}
              <svg
                viewBox="0 0 24 24"
                width="34"
                height="34"
                fill="#00c8d4"
                aria-hidden="true"
                className="animate-senala flex-shrink-0"
              >
                <path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V11h.5V7.5a1.5 1.5 0 0 1 3 0V11h.5V9a1.5 1.5 0 0 1 3 0v6.2c0 3.2-2.3 5.8-5.6 5.8h-1.6c-2 0-3.5-.9-4.6-2.6l-2.8-4.6a1.5 1.5 0 0 1 2.3-1.9L8 13.4V5.5a1.5 1.5 0 0 1 3 0V11H9z" />
              </svg>
              <a
                href="https://www.linkedin.com/company/mercalia-connect-group"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="MERCALIA Connect Group en LinkedIn"
                title="LinkedIn"
                className="inline-flex items-center gap-3 px-5 py-3.5 rounded-xl text-base font-bold text-white hover:text-[#00c8d4] transition-all duration-200 hover:scale-[1.04]"
                style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.28)' }}
              >
                <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
                  <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
                </svg>
                LinkedIn
              </a>

              <a
                href="https://connectamericas.com/es/company/mercalia-connect-group-llc"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Perfil verificado de MERCALIA Connect Group en ConnectAmericas"
                title="Empresa verificada en ConnectAmericas"
                className="inline-flex items-center gap-3 px-5 py-3.5 rounded-xl text-base font-bold text-white hover:text-[#00c8d4] transition-all duration-200 hover:scale-[1.04] animate-latido"
                style={{ background: 'rgba(0,200,212,0.16)', border: '1px solid rgba(0,200,212,0.50)' }}
              >
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                ConnectAmericas
              </a>
            </div>
          </div>

          {/* Nav links */}
          <nav className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                className="text-sm font-medium text-white/50 hover:text-[#00c8d4] transition-colors duration-200"
              >
                {t(link?.labelKey)}
              </Link>
            ))}
          </nav>

          {/* Legal links */}
          <div className="flex flex-col gap-2">
            <Link
              href="/verify"
              className="text-sm font-semibold text-[#00c8d4] hover:text-white transition-colors"
            >
              {t('footer.verify')}
            </Link>
            <a
              href="https://connectamericas.com/es/company/mercalia-connect-group-llc"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-white/40 hover:text-[#00c8d4] transition-colors"
            >
              {t('footer.ca')}
            </a>
            <Link href="/privacy" className="text-sm font-medium text-white/40 hover:text-[#00c8d4] transition-colors">{t('footer.privacy')}</Link>
            <Link href="/terms" className="text-sm font-medium text-white/40 hover:text-[#00c8d4] transition-colors">{t('footer.terms')}</Link>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30">{t('footer.rights')}</p>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00c8d4] opacity-60" />
            <span className="text-xs text-white/25">New Mexico, USA · BOI filed with FinCEN · D-U-N-S® 149902101</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
